import { useMemo, useState } from "react";
import { Input, Collapse, Empty, Tag, Typography, Space, Button } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import faqData, { FAQ_CATEGORIES } from "./faqData";
import "./faq.css";

const { Text } = Typography;

/**
 * Searchable Help & FAQ. Filters across question, answer, keywords and category;
 * results are grouped by category. Used inside the Help & FAQ modal.
 */
export default function HelpFaq() {
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return faqData.filter((item) => {
      if (activeCategory && item.category !== activeCategory) return false;
      if (!q) return true;
      const hay = [
        item.question,
        item.answer,
        item.category,
        ...(item.keywords || []),
      ]
        .join(" ")
        .toLowerCase();
      return q.split(/\s+/).every((term) => hay.includes(term));
    });
  }, [query, activeCategory]);

  const grouped = useMemo(() => {
    const map = {};
    for (const item of filtered) {
      (map[item.category] = map[item.category] || []).push(item);
    }
    // Preserve the canonical category order.
    return FAQ_CATEGORIES.filter((c) => map[c]).map((c) => [c, map[c]]);
  }, [filtered]);

  return (
    <div>
      <Input
        size="large"
        allowClear
        autoFocus
        prefix={<SearchOutlined />}
        placeholder="Search help — e.g. 'workflow limit', 'customer code', 'create invoice'"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        style={{ marginBottom: 12 }}
      />

      <Space wrap size={[6, 6]} style={{ marginBottom: 12 }}>
        <Button
          size="small"
          type={activeCategory ? "default" : "primary"}
          onClick={() => setActiveCategory(null)}
        >
          All
        </Button>
        {FAQ_CATEGORIES.map((c) => (
          <Button
            key={c}
            size="small"
            type={activeCategory === c ? "primary" : "default"}
            onClick={() => setActiveCategory(activeCategory === c ? null : c)}
          >
            {c}
          </Button>
        ))}
      </Space>

      <div style={{ maxHeight: "55vh", overflowY: "auto" }}>
        {grouped.length === 0 ? (
          <Empty description={`No help found for “${query}”`} />
        ) : (
          grouped.map(([category, items]) => (
            <div key={category} style={{ marginBottom: 16 }}>
              <Text type="secondary" strong style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5 }}>
                {category}
              </Text>
              <Collapse
                className="faq-collapse"
                style={{ marginTop: 6 }}
                items={items.map((item) => ({
                  key: item.id,
                  label: <span className="faq-question">{item.question}</span>,
                  children: <div className="faq-answer">{item.answer}</div>,
                  extra: <Tag color="blue">{item.category}</Tag>,
                }))}
              />
            </div>
          ))
        )}
      </div>

      <Text type="secondary" style={{ fontSize: 12 }}>
        Showing {filtered.length} of {faqData.length} topics.
      </Text>
    </div>
  );
}
